import { ContextMenuItemIndicator } from '@radix-ui/react-context-menu'
import { withLDConsumer } from 'launchdarkly-react-client-sdk'
import { observer } from 'mobx-react-lite'
import { clone, detach } from 'mobx-state-tree'
import React, { FC, useContext, useState } from 'react'
import ArpRegistry from '../../../../features/arp-registry/ArpRegistry'
import { GRAY_COLORS } from '../../../lib/stitches'
import { useTreeState } from '../../../stores/Root'
import { IClipStore } from '../../../stores/song/clip/ClipStore'
import { IAudioStemStore } from '../../../stores/song/stem/AudioStemStore'
import { undoManager } from '../../../stores/StudioStore'
import CenteredLoadingAnimation from '../../core/CenteredLoadingAnimation'
import { Box, Column, Row } from '../../core/Components'
import { CheckIcon, StretchIcon } from '../../core/icons'
import { getClipColor, gray1, gray4 } from '../../platform/color'
import AudioVisualizer from '../../player/AudioVisualizer'
import { ClipboardContext, TrackRefsContext } from '../Context'
import ContextMenu from '../controls/ContextMenu'
import { RecordMark } from '../controls/RecordMarker'
import useAudioStemBar from '../hooks/useAudioStemBar'
import useAudioStemTrack from '../hooks/useAudioStemTrack'
import useClip from '../hooks/useClip'
import useClipHandle from '../hooks/useClipHandle'
import { CopyTypes } from '../StudioConstants'

interface ClipContextMenuProps {
  currentClip: IClipStore
  stemIndex: number
}
const ClipContextMenu: FC<ClipContextMenuProps> = observer(({ currentClip, stemIndex }) => {
  const { studio } = useTreeState()
  const { clipboard, setClipboard } = useContext(ClipboardContext)
  const currentClipSelected = studio.selectedClipIDs.includes(currentClip.uniqueId)

  const stem = studio.song.stems[stemIndex] as IAudioStemStore
  const isRemixed = stem.isRemixed
  const isSnappingToGrid = currentClip.snapToGrid
  const toggleSnapToGrid = () => currentClip.toggleSnapToGrid()
  const deleteClip = () => {
    if (currentClipSelected) {
      studio.deleteSelectedClips()
    } else {
      stem.removeClipByID(currentClip.uniqueId)
    }
  }
  const duplicateClip = () => {
    undoManager.startGroup(() => {
      studio.disableAudioUpdates()
      if (currentClipSelected) {
        const selectedClips = studio.selectedClipIDs.map((clipID) =>
          studio.song.getClipByID(clipID)
        )
        const newStart = Math.max.apply(
          null,
          selectedClips.map((clip) => clip.endInComposition48n)
        )
        const offset = Math.min.apply(
          null,
          selectedClips.map((clip) => clip.startInComposition48n)
        )
        selectedClips.forEach((clip) => {
          const newClip = clone(clip)
          newClip.generateClipID()
          const newClipStart = newClip.startInComposition48n
          newClip.move48n(newStart - offset, studio.song.songLengthIn48n)
          studio.addClipToStem(studio.song.getClipParentStemIndex(clip.uniqueId), newClip)
        })
      } else {
        stem.duplicateClipByID(currentClip.uniqueId)
      }
      studio.enableAudioUpdates()
      undoManager.stopGroup()
    })
  }
  const copyClip = () => {
    undoManager.withoutUndo(() => {
      const clipCopies: IClipStore[][] = []
      if (currentClipSelected) {
        const parentStemIndexes = studio.selectedClipIDs.map((clipID) =>
          studio.song.getClipParentStemIndex(clipID)
        )
        const minParentStemIndex = Math.min.apply(null, parentStemIndexes)
        const maxParentStemIndex = Math.max.apply(null, parentStemIndexes)
        for (let i = 0; i <= maxParentStemIndex - minParentStemIndex; i++) {
          clipCopies.push([])
        }

        studio.selectedClipIDs.forEach((clipID) => {
          const parentStemIndex = studio.song.getClipParentStemIndex(clipID)
          const clip = studio.song.getClipByID(clipID)
          const clipCopy = clone(clip)
          detach(clipCopy)
          clipCopy.generateClipID()
          clipCopies[parentStemIndex - minParentStemIndex].push(clipCopy)
        })
      } else {
        const newClip = clone(currentClip)
        detach(newClip)
        newClip.generateClipID()
        clipCopies.push([newClip])
      }

      /** The Clipboard value for CopyTypes.CLIPS is stored as an 2D array of Clips.
       * The structure is based on the parent stemIndex of the copied clips, as we need
       * to maintain that structure when we paste. Examples:
       * 1. A single clip is copied -- structure will be [[ClipA]] since we are only copying from 1 stem
       * 2. A clip from stemIndex 0 and a clip from stemIndex 1 are copied -- the structure will be [[ClipA], [clipB]], where
       * the 0th index represents stemIndex 0, and the 1th index represents stemIndex 1
       * 3. A clip from stemIndex 0 and a clip from stemIndex 2 are copied -- the structure will be [[ClipA], [], [clipB]]
       * 4. (More common/complicated case) 2 clips are copied from stemIndex 2, 1 clip is copied from stemIndex 4, and 2 clips
       * are copied from stemIndex 5 -- the structure will be [[ClipA, ClipB], [], [ClipC], [ClipD, clipE]] -- you can see that
       * we normalize the lowest stemIndex to be the 0th index of the copy array
       */
      setClipboard({
        type: CopyTypes.CLIPS,
        value: clipCopies
      })
    })
  }

  return (
    <ContextMenu.Portal>
      <ContextMenu.Content>
        {!isRemixed && <ContextMenu.Item onClick={copyClip}>Copy</ContextMenu.Item>}
        <ContextMenu.Item onClick={duplicateClip}>Duplicate</ContextMenu.Item>
        <ContextMenu.Item onClick={deleteClip}>Delete</ContextMenu.Item>
        <ContextMenu.CheckboxItem onCheckedChange={toggleSnapToGrid} checked={isSnappingToGrid}>
          <ContextMenuItemIndicator>
            <CheckIcon />
          </ContextMenuItemIndicator>
          Snap to Grid
        </ContextMenu.CheckboxItem>
      </ContextMenu.Content>
    </ContextMenu.Portal>
  )
})

interface AudioStemBarMenuProps {
  isRemixed: boolean
  pasteClip: () => void
  loopSection: () => void
}

const AudioStemBarContextMenu: FC<AudioStemBarMenuProps> = observer(
  ({ isRemixed, pasteClip, loopSection }) => {
    const { clipboard } = useContext(ClipboardContext)
    return (
      <ContextMenu.Portal>
        <ContextMenu.Content>
          {clipboard &&
            (clipboard.type === CopyTypes.CLIP || clipboard.type === CopyTypes.CLIPS) &&
            !isRemixed && <ContextMenu.Item onClick={pasteClip}>Paste</ContextMenu.Item>}
          <ContextMenu.Item onClick={loopSection}>Loop Section</ContextMenu.Item>
        </ContextMenu.Content>
      </ContextMenu.Portal>
    )
  }
)

interface HandleProps {
  onMove: (deltaPX: number) => boolean
  instrumentLoopWidthPX?: number
  css?: {}
  dependencies?: any[]
  snapToGrid?: boolean
}
export const StretchHandle: FC<HandleProps> = observer(
  ({ onMove, instrumentLoopWidthPX, css, dependencies, snapToGrid }) => {
    const { handleRef } = useClipHandle(onMove, instrumentLoopWidthPX, dependencies, snapToGrid)
    return (
      <Box
        css={{
          cursor: 'ew-resize',
          zIndex: '4',
          position: 'absolute',
          top: '0',
          right: '5px',
          color: gray1,
          width: '11px',
          height: '7px',
          ...css
        }}
        ref={handleRef}
        title="Stretch"
      >
        <StretchIcon width="100%" height="100%" />
      </Box>
    )
  }
)

export const ClipHandle: FC<HandleProps> = observer(
  ({ onMove, instrumentLoopWidthPX, css, dependencies, snapToGrid }) => {
    const { handleRef } = useClipHandle(onMove, instrumentLoopWidthPX, dependencies, snapToGrid)
    return (
      <Box
        css={{
          width: '2px',
          minWidth: '2px',
          height: '100%',
          cursor: 'col-resize',
          zIndex: '4',
          position: 'absolute',
          ...css
        }}
        ref={handleRef}
        title="Crop"
      />
    )
  }
)

interface ClipProps {
  clip: IClipStore
  stemIndex: number
  instrumentLoopWidthPX: number
  color: string
}
const Clip: FC<ClipProps> = observer(({ clip, stemIndex, instrumentLoopWidthPX, color }) => {
  const [isHover, setIsHover] = useState<boolean>(false)
  const { refs: instrumentTrackRefs } = useContext(TrackRefsContext)
  const { clipboard, setClipboard } = useContext(ClipboardContext)
  const {
    audioDataArr,
    clipContainer,
    onResizeLeft,
    onResizeRight,
    onStretch,
    left,
    width,
    title,
    startOffsetPx,
    endOffsetPx
  } = useClip(clip, instrumentLoopWidthPX, stemIndex, instrumentTrackRefs)
  const { studio } = useTreeState()
  const stem: IAudioStemStore = studio.song.stems[stemIndex] as IAudioStemStore
  const isSelected = studio.selectedClipIDs.includes(clip.uniqueId)
  const clipIsProcessing = clip && studio.processingClips.has(clip.uniqueId)

  const clipColor = getClipColor(clip)
  return (
    <ContextMenu.Root>
      <ContextMenu.Trigger>
        <Row
          css={{
            position: 'absolute',
            overflow: 'hidden',
            borderRadius: '5px',
            height: '100%',
            padding: '5px 0px',
            backgroundColor: clipIsProcessing ? gray4 : clipColor,
            left: `${left}px`,
            width: `${width}px`,
            opacity: clipIsProcessing ? '30%' : isSelected ? '80%' : '50%',
            zIndex: isSelected ? '2' : '1',
            backgroundImage: isSelected && `linear-gradient(to bottom, ${clipColor}, transparent)`
          }}
          onMouseEnter={() => setIsHover(true)}
          onMouseLeave={() => setIsHover(false)}
          ref={clipContainer}
          verticalCenter
          justifySpaceBetween
        >
          {!clip.initializing && ArpRegistry.readyForStudioUse && (
            <>
              {/* show loader if clip is processing */}
              {clipIsProcessing && (
                <Row
                  css={{ width: '100%', height: '100%', position: 'absolute' }}
                  verticalCenter
                  horizontalCenter
                >
                  <CenteredLoadingAnimation />
                </Row>
              )}
              {/* Can't show the stretch handle if the container is too small to fit it */}
              {clipContainer?.current &&
                clipContainer.current.offsetWidth >= 20 &&
                clip.canBeStretchedOrTransposed && (
                  <StretchHandle
                    onMove={onStretch}
                    instrumentLoopWidthPX={instrumentLoopWidthPX}
                    snapToGrid={clip.snapToGrid}
                    css={{ opacity: isHover || isSelected ? '100%' : '0%' }}
                  />
                )}
              <ClipHandle
                onMove={onResizeLeft}
                instrumentLoopWidthPX={instrumentLoopWidthPX}
                snapToGrid={clip.snapToGrid}
                css={{ left: 0 }}
              />
              <Column
                style={{
                  height: '90%',
                  minWidth: `calc(100% + ${startOffsetPx / clip.warp + endOffsetPx}px)`,
                  marginLeft: `${-startOffsetPx / clip.warp}px`,
                  marginRight: `${-endOffsetPx}px`
                }}
              >
                <AudioVisualizer
                  audioBuffer={null}
                  audioArray={audioDataArr}
                  options={{
                    color: '#000000',
                    segmentWidth: 0.25,
                    segmentSpacing: 0
                  }}
                />
              </Column>
              <ClipHandle
                onMove={onResizeRight}
                instrumentLoopWidthPX={instrumentLoopWidthPX}
                snapToGrid={clip.snapToGrid}
                css={{ right: 0 }}
              />
            </>
          )}
        </Row>
      </ContextMenu.Trigger>

      <ClipContextMenu currentClip={clip} stemIndex={stemIndex} />
    </ContextMenu.Root>
  )
})

interface AudioStemBarProps {
  instrumentLoopWidthPX: number
  section: number
  stemIndex: number
}
const AudioStemBar: FC<AudioStemBarProps> = observer(
  ({ instrumentLoopWidthPX, section, stemIndex }) => {
    const { studio } = useTreeState()
    const { fill, darkFill, ref, barCount, pasteClip, loopSection } = useAudioStemBar(
      stemIndex,
      section,
      instrumentLoopWidthPX
    )
    const stem = studio.song.stems[stemIndex]

    return (
      <ContextMenu.Root>
        <Row
          css={{
            minWidth: `${instrumentLoopWidthPX}px`,
            maxWidth: `${instrumentLoopWidthPX}px`,
            background: section % 2 === 0 ? GRAY_COLORS.gray01 : GRAY_COLORS.gray005,
            borderBottom: `1px solid ${gray4}`
          }}
          ref={ref}
        >
          <ContextMenu.Trigger>
            <Row css={{ width: `${instrumentLoopWidthPX}px`, height: '100%' }}>
              {new Array(barCount).fill(0).map((_, index) => {
                const color = (index + 1) % 8 === 0 ? darkFill : fill
                return <RecordMark height="100%" fill={color} key={index} />
              })}
            </Row>
          </ContextMenu.Trigger>
          <AudioStemBarContextMenu
            isRemixed={stem.isRemixed}
            pasteClip={pasteClip}
            loopSection={loopSection}
          />
        </Row>
      </ContextMenu.Root>
    )
  }
)

interface AudioStemTrackProps {
  instrumentLoopWidthPX: number
  stemID: string
  trackHeight: string
}
const AudioStemTrack = (
  { instrumentLoopWidthPX, stemID, trackHeight }: AudioStemTrackProps,
  ref: React.Ref<HTMLDivElement>
) => {
  const { studio } = useTreeState()
  const stemIndex = studio.song.getStemIndexByID(stemID)
  const { studioBarsArray, color, clips } = useAudioStemTrack(stemIndex)

  return (
    <Row css={{ height: trackHeight, position: 'relative' }} id={`audio-stem-${stemID}`} ref={ref}>
      {studioBarsArray.map((_, section) => (
        <AudioStemBar
          instrumentLoopWidthPX={instrumentLoopWidthPX}
          section={section}
          stemIndex={stemIndex}
          key={section}
        />
      ))}
      {clips.map((clip: IClipStore) => (
        <Clip
          clip={clip}
          instrumentLoopWidthPX={instrumentLoopWidthPX}
          stemIndex={stemIndex}
          color={color}
          key={clip.uniqueId}
        />
      ))}
    </Row>
  )
}

export default withLDConsumer()(observer(React.forwardRef(AudioStemTrack)))
